import React, { useEffect, useMemo } from 'react'

import CalendarDay from '@/components/integration/nylas/scheduler/components/calendarDayPicker/CalendarDay'
import CalendarDayButton from '@/components/integration/nylas/scheduler/components/calendarDayPicker/CalendarDayButton'
import {
  CalendarMonth,
  CalendarMonthGrid,
  CalendarNextMonthButton,
  CalendarPreviousMonthButton,
  CalendarRoot,
  CalendarWeek,
  CalendarWeekday,
  CalendarWeekdays,
  CalendarWeeks,
} from '@/components/integration/nylas/scheduler/components/calendarDayPicker/calendarSlots'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import { getCalendarAnchorDate } from '@/components/integration/nylas/scheduler/helpers'
import usePrefetchMonthAvailability from '@/components/integration/nylas/scheduler/hooks/usePrefetchMonthAvailability'
import { Calendar } from '@/components/ui/calendar'
import availabilityService from '@/lib/services/availabilityService'

type CalendarComponents = NonNullable<
  React.ComponentProps<typeof Calendar>['components']
>

const calendarComponents: CalendarComponents = {
  Root: CalendarRoot,
  Month: CalendarMonth,
  MonthGrid: CalendarMonthGrid,
  NextMonthButton: CalendarNextMonthButton,
  PreviousMonthButton: CalendarPreviousMonthButton,
  Weeks: CalendarWeeks,
  Weekdays: CalendarWeekdays,
  Weekday: CalendarWeekday,
  Week: CalendarWeek,
  Day: CalendarDay,
  DayButton: CalendarDayButton,
}

const CalendarBasedPicker: React.FC = () => {
  const {
    selectedDate,
    selectedDateRange,
    selectDate,
    setSelectedDateRange,
    availabilityFilter,
  } = useSchedulerSelection()
  const {
    owningEntityType,
    owningEntityId,
    maxBookingNoticeDays,
    availabilitySource,
  } = useSchedulerConfig()

  const prefetchMonthAvailability = usePrefetchMonthAvailability(
    owningEntityType,
    owningEntityId,
    availabilityFilter,
    availabilitySource,
  )

  const month = selectedDateRange.start

  const { startMonth, endMonth } = useMemo(() => {
    const today = new Date()
    const firstOfCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
    )

    if (maxBookingNoticeDays === undefined) {
      return { startMonth: firstOfCurrentMonth, endMonth: undefined }
    }

    const lastBookableDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + maxBookingNoticeDays,
    )
    const firstOfLastBookableMonth = new Date(
      lastBookableDay.getFullYear(),
      lastBookableDay.getMonth(),
      1,
    )

    return {
      startMonth: firstOfCurrentMonth,
      endMonth: firstOfLastBookableMonth,
    }
  }, [maxBookingNoticeDays])

  useEffect(() => {
    // Custom sources (e.g. rescheduling) are costly per request - each one
    // temporarily frees the existing meeting in the adviser's calendar - so
    // only fetch the month being viewed
    if (availabilitySource) {
      return
    }

    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1)
    if (endMonth !== undefined && nextMonth.getTime() > endMonth.getTime()) {
      return
    }
    prefetchMonthAvailability(nextMonth)
  }, [month, endMonth, prefetchMonthAvailability, availabilitySource])

  function changeMonth(newMonth: Date) {
    selectDate(null)
    setSelectedDateRange(availabilityService.getMonthDateRange(newMonth))
  }

  function handleDateSelected(date: Date) {
    selectDate(getCalendarAnchorDate(date))

    if (date.getMonth() !== month.getMonth()) {
      changeMonth(new Date(date.getFullYear(), date.getMonth(), 1))
    }
  }

  return (
    <Calendar
      mode="single"
      selected={selectedDate || undefined}
      month={month}
      startMonth={startMonth}
      endMonth={endMonth}
      numberOfMonths={1}
      weekStartsOn={1}
      showOutsideDays={true}
      captionLayout="dropdown"
      onMonthChange={(date) => changeMonth(date)}
      onDayClick={(date) => handleDateSelected(date)}
      className="mx-auto p-0"
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'long' }),
        formatWeekdayName: (date) =>
          date.toLocaleString('default', { weekday: 'short' }).toUpperCase(),
      }}
      components={calendarComponents}
    />
  )
}

export default CalendarBasedPicker
